import { useFetch } from "../../hooks/api";
import { useAppSelector } from "../../redux/hooks";
import type { RowData, ColumnFilter } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useQueryParams from "../../hooks/useQueryParams";
import { useDates } from "../../dates/DatesContext";
import { useAcosOrRoas } from "../../utils/acosOrRoas";
import type { Instance } from "react-table-6";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useTargetBulkActions from "../../hooks/useTargetBulkActions";
import { useAddTargetsToAd } from "../advertising/api/useAddTargets";
import getMetricSpec from "../merchandising/utils/metrics";
import useSelectedMetrics from "../../components/custom/merchandise/hooks/useSelectedMetrics";
import { snakeCaseToSentenceCase } from "../../utils/formatText";
import useCustomTableFilters from "../../hooks/useCustomTableFilters";
import Loading from "../../components/core/blocks/Loading";
import { Col, Nav, Row } from "react-bootstrap";
import MultiMetricChart from "../../components/custom/merchandise/MultiMetricChart";
import { css } from "../../../styled-system/css";
import ExpanderTable from "../merchandising/components/tables/BulkActionsTable";
import KeywordCell from "../../components/custom/category/advertisements/KeywordCell";
import { useFormatTargetText } from "../../utils/useFormatTargetText";
import TrellisLink from "../../components/core/basic/TrellisLink";
import { getDashboardTo } from "../../views/AdvertisingOverview";
import { TargetBadges } from "../../components/custom/category/advertisements/TargetBadges";
import { Flex } from "../../../styled-system/jsx";
import { useTargetLifecycleFilters } from "../advertising/filters/targetLifecycleFilters";
import { type GVAdvertisement } from "../advertising/types";
import { useTargetReport } from "../perf_reports/api/useTargetReport";
import { useGetV4AdvReportByAd, type AdvertisementQueryV4Row } from "../perf_reports/api/ads/v4AdvReportByAd";
import { MetricLabel } from "../../components/i18n/MetricLabel";
import type { ExpanderProps, ExpanderColumnSpec, ExpanderQueryData, KeywordsResponse } from "./types";
import { Checkbox } from "../../components/fields/base/Checkbox";
import AddTargetsWidget from "../../components/custom/category/advertisements/AddTargetsWidget";
import { useSimpleCan } from "../../config/casl/casl";
import { useGetV4SearchTermPerformanceByDate } from "../str/api/v4SearchTermPerformanceByDate";
import { extractAsin } from "../../utils/extractAsin";
import { getValidKeywordToNegate } from "../advertising/utils/getValidKeywordsToNegate";
import { type AppRow } from "../../components/table/tableFeatures";

const QueryExpander = <T extends RowData>({
  dims,
  query,
  keyword_text,
  match_type,
  row,
  context,
  renderNegativeKeywordsTab = false,
  isFlattenedSearchTermReport = false,
  useClickhouseSearchTermReportData = false,
}: ExpanderProps<T>) => {
  const { t } = useTranslation(["query_funnel"]);
  const { SEARCH_TERM_REPORT_MATCH_TYPE_FILTER } = useTargetLifecycleFilters();
  const formatTargetText = useFormatTargetText();
  const [selectedTargets, setSelectedTargets] = useState<string[] | number[]>([]);
  const [selectedMatchTypes, setSelectedMatchTypes] = useState<string[]>([]);

  const tableRef = useRef<Instance<KeywordsResponse["keywords"][number]> | null>(null);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [tab, setTab] = useQueryParams("etab", "keyword");
  const { start, end, preStart, preEnd, group } = useDates();
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const {
    adv_keyword_ids,
    adv_target_ids,
    query: rowQuery,
    keyword_text: rowKeywordText,
  } = row.original as {
    adv_keyword_ids: string[];
    adv_target_ids: string[];
    query: string;
    keyword_text: string;
    match_type: string;
  };

  const acosOrRoas = useAcosOrRoas();
  let filters = `date__gte:${start.format("YYYY-MM-DD")},date__lte:${end.format("YYYY-MM-DD")},marketplace:${marketPlace.marketPlace}`;
  let clickhouseTimelineQueryFilters: ColumnFilter[] = [];
  if (dims === "query") {
    filters = `${filters},query:${query}`;
    clickhouseTimelineQueryFilters = [{ id: "query", value: { op: "=", value: query } }];
  } else if (dims === "negative-keyword") {
    filters = `${filters},keyword_text:${keyword_text},match_type:negative`;
  } else if (dims === "keyword_text,query,match_type,campaign_id") {
    filters = `${filters},query:${query},keyword_text:${keyword_text},match_type:${match_type}`;
    clickhouseTimelineQueryFilters = [
      { id: "query", value: { op: "=", value: query } },
      { id: "keyword_text", value: { op: "=", value: keyword_text } },
      { id: "match_type", value: { op: "=", value: match_type } },
    ];
  } else if (
    [
      "keyword_text,query,match_type,campaign_id,adv_keyword_id",
      "keyword_text,query,match_type,campaign_id,adv_target_id",
    ].includes(dims)
  ) {
    clickhouseTimelineQueryFilters = [
      { id: "query", value: { op: "=", value: query } },
      { id: "keyword_text", value: { op: "=", value: keyword_text } },
    ];
  } else {
    filters = `${filters},keyword_text:${keyword_text},match_type:${match_type}`;
    clickhouseTimelineQueryFilters = [
      { id: "keyword_text", value: { op: "=", value: keyword_text } },
      { id: "match_type", value: { op: "=", value: match_type } },
    ];
  }

  let keywordText = dims === "query" ? query : keyword_text;
  const isAsin = !!extractAsin(String(keywordText ?? ""));

  if (isAsin) {
    const asin = extractAsin(String(keywordText ?? ""));
    if (asin !== null) {
      keywordText = asin;
    }
  }

  // TODO: convert this to use Clickhouse endpoint once target/keyword and ad ID fields added
  const { data: _data, isLoading } = useFetch<Record<string, unknown>[]>(
    ["searchTermReport", filters, group, start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")],
    `/api/data_report_v2/searchtermreport/stats/`,
    {
      dimensions: group,
      filters,
    },
    {
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 5,
      select: (res) => res.data,
      enabled: !useClickhouseSearchTermReportData,
    },
  );

  const byDateOptionsGetter = useGetV4SearchTermPerformanceByDate();

  const timelineQuery = useQuery(
    byDateOptionsGetter({
      filters: clickhouseTimelineQueryFilters,
    }),
  );

  const data = useMemo(() => {
    if (useClickhouseSearchTermReportData) {
      return timelineQuery.data?.data || [];
    }
    return _data?.map((row) => {
      return {
        start_date:
          typeof row.date === "string" ? row.date
          : row.date instanceof Date ? row.date.toISOString().slice(0, 10)
          : "",
        ...row,
      };
    });
  }, [_data, timelineQuery, useClickhouseSearchTermReportData]);

  const { data: targetData, isLoading: isTargetDataLoading } = useTargetReport(
    adv_target_ids.filter((k) => k),
    "target",
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    group,
  );

  const { data: keywordData, isLoading: isKeywordDataLoading } = useTargetReport(
    Array.isArray(adv_keyword_ids) ? adv_keyword_ids.filter((k) => k) : [],
    "keyword",
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    group,
  );

  const { bulkActionOptions } = useTargetBulkActions(
    selectedTargets,
    setSelectedTargets,
    tab,
    tab === "target" ? targetData : keywordData,
  );

  const byAdGetter = useGetV4AdvReportByAd();

  const adIdsFromData = useMemo(() => {
    const data = tab === "target" ? targetData : keywordData;
    const keywords =
      data && typeof data === "object" && "keywords" in data ?
        (data as { keywords: { advertisement_id: string }[] }).keywords
      : [];
    return Array.from(new Set(keywords.map((k) => k.advertisement_id).filter(Boolean)));
  }, [keywordData, targetData, tab]);

  const advertisementQuery = useQuery({
    ...byAdGetter({
      pagination: { pageIndex: 0, pageSize: adIdsFromData.length },
      sorting: [],
      filters: adIdsFromData.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: adIdsFromData } }] : [],
      search: "",
    }),
    enabled: adIdsFromData.length > 0,
  });

  const isAdsDataLoading = adIdsFromData.length > 0 && (advertisementQuery.isPending || advertisementQuery.isFetching);

  const addTargetsMutation = useAddTargetsToAd();
  const negateTargets = () => {
    for (let i = 0; i < selectedTargets.length; i++) {
      const data = tab === "target" ? targetData : keywordData;
      const rowDetails = (
        data as { keywords: { id: string; advertisement_id: string; ad_group_id: string }[] }
      ).keywords.find((target) => target.id === selectedTargets[i]);

      const body = {
        data: {
          advertisement: Number(rowDetails?.advertisement_id),
          ad_group: rowDetails?.ad_group_id,
          target_type: isAsin ? "product" : "keyword",
          channel: "amazon",
          action: "create_targets" as const,
          targets:
            isAsin ?
              {
                products: [String(keywordText)],
                targetTypes: ["negative_product"],
              }
            : [
                {
                  text: String(keywordText),
                  matchTypes: selectedMatchTypes,
                },
              ],
        },
      };

      addTargetsMutation.mutate(body);
    }
  };

  const targetText = dims === "query" ? rowQuery : rowKeywordText;
  const isCategoryTarget = targetText.includes("category=");

  const additionalBulkActions =
    isCategoryTarget ?
      []
    : [
        {
          label:
            isAsin ?
              t(($) => $.query_funnel.expander["Negate Query ASIN"])
            : t(($) => $.query_funnel.expander["Negate Query Text"]),
          action: () => negateTargets(),
          actionFormFields:
            isAsin ? null : (
              <Flex direction={"column"} alignItems={"center"}>
                <p>{t(($) => $.query_funnel.expander["Select the match types you want to negate:"])}</p>
                <div className={css({ paddingX: "large" })}>
                  {["negative_exact", "negative_phrase"].map((matchType) => (
                    <Checkbox
                      isDisabled={!getValidKeywordToNegate(matchType, keywordText ? String(keywordText) : "")}
                      key={matchType}
                      label={snakeCaseToSentenceCase(matchType)}
                      value={selectedMatchTypes.includes(matchType)}
                      onChange={(checked) =>
                        setSelectedMatchTypes((prev) =>
                          checked ? [...prev, matchType] : prev.filter((t) => t !== matchType),
                        )
                      }
                    />
                  ))}
                </div>
              </Flex>
            ),
          disableConfirmButton: isAsin ? false : selectedMatchTypes.length === 0,
          confirmationMessage: (
            <p className="fs-4 py-2">
              {isAsin ?
                t(($) => $.query_funnel.expander['Are you sure you want to negate the ASIN "{value}"?'], {
                  value: String(keywordText).toUpperCase(),
                })
              : t(($) => $.query_funnel.expander['Are you sure you want to negate the keyword "{value}"?'], {
                  value: String(keywordText),
                })
              }
            </p>
          ),
        },
      ];

  const metrics = useMemo(
    () => [
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("clicks"),
      getMetricSpec("impressions"),
      getMetricSpec("ctr"),
      getMetricSpec("conversion_rate"),
      getMetricSpec("cpc"),
      getMetricSpec("viewable_impressions"),
      getMetricSpec("viewability_rate"),
      getMetricSpec("view_click_through_rate"),
    ],
    [acosOrRoas],
  );

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["impressions", "ad_spend", "ad_sales"],
    "searchTermReportExpander",
  );

  const columns = useMemo<ExpanderColumnSpec[]>(() => {
    const perfCol = acosOrRoas({ key: "acos", compareKey: "acos_delta" }, { key: "roas", compareKey: "roas_delta" });
    const cols: ExpanderColumnSpec[] = [
      {
        key: "ad_group_name",
        compareKey: null,
        options: {
          columnTitle: t(($) => $.query_funnel.expander["Ad Group Name"]),
          metric: {
            id: "ad_group_name",
            format: "text",
          },
          hideFilter: true,
          overflowCell: true,
          additionalStyles: { paddingTop: "20px" },
          formatter: (value: string) => value,
        },
      },
      {
        key: "target_bid",
        compareKey: null,
        options: {
          columnTitle: t(($) => $.query_funnel.expander.Bid),
          className: "text-end",
          overrideKey: "manual_bid",
          strikeThroughText: (row: unknown) => {
            const typedRow = row as AppRow<ExpanderQueryData>;
            const state = typedRow.original.state;

            if (typeof state !== "string") return "";

            if (state.toLowerCase().includes("negative")) {
              return "text-decoration-line-through";
            }

            return "";
          },
        },
      },
      {
        key: "target_source",
        compareKey: null,
        options: {
          hideFilter: true,
          formatter: (value: string) =>
            value === "GV_DISCOVERY" ? "initial"
            : value === "harvested" ? "harvesting"
            : snakeCaseToSentenceCase(value).toLowerCase(),
        },
      },
      {
        key: "creation_date",
        compareKey: null,
        options: {
          columnTitle: t(($) => $.query_funnel.expander["Created On"]),
          metric: {
            id: "creation_date",
            accessor: (d: { creation_date?: string | null }) =>
              d.creation_date ? new Date(d.creation_date).getTime() : null,
          },
          formatter: (v: string | null | undefined) => v ?? "",
          hideFilter: true,
          checked: false,
        },
      },

      {
        key: "match_type",
        compareKey: null,
        options: {
          width: 150,
          hideFilter: true,
          formatter: (v: string | null | undefined) => {
            return snakeCaseToSentenceCase(v).toLowerCase().includes("negative") ?
                snakeCaseToSentenceCase(v).toLowerCase().replace("negative", "negative ")
              : snakeCaseToSentenceCase(v).toLowerCase();
          },
        },
      },
      {
        key: "target_state",
        compareKey: null,
        options: {
          checked: true,
          columnTitle: t(($) => $.query_funnel.expander.State),
          metric: {
            id: "target_state",
            accessor: (d) => d.target_state,
          },
          formatter: (v: string) => snakeCaseToSentenceCase(v).toLowerCase(),
          filterType: "select",
          hideFilter: true,
        },
      },

      { key: "impressions", compareKey: "impressions_percent" },
      { key: "clicks", compareKey: "clicks_percent" },
      { key: "orders", compareKey: "conversion_percent" },
      { key: "ad_spend", compareKey: "ad_spend_percent" },
      { key: "ad_sales", compareKey: "ad_sales_percent" },
      perfCol,
      { key: "cpc", compareKey: "cpc_delta" },
      { key: "conversion_rate", compareKey: "conversion_rate_delta" },
      { key: "ctr", compareKey: "ctr_delta" },
      { key: "viewable_impressions", compareKey: "viewable_impressions_percent" },
      { key: "viewability_rate", compareKey: "viewability_rate_delta" },
      { key: "view_click_through_rate", compareKey: "view_click_through_rate_delta" },
    ];
    return cols;
  }, [acosOrRoas, t]);
  const tableId = dims === "query" ? `${context}-str-report` : `${context}-str`;
  const filterProps = useCustomTableFilters(tableId);
  const adsMapping: { [key: string]: GVAdvertisement } = useMemo(() => {
    const ads = advertisementQuery.data?.data ?? [];
    return ads.reduce(
      (acc: { [key: string]: GVAdvertisement }, row: AdvertisementQueryV4Row) => {
        acc[row.ad_id.toString()] = row;
        return acc;
      },
      {} as { [key: string]: GVAdvertisement },
    );
  }, [advertisementQuery.data?.data]);

  const tableData = useMemo(() => {
    const data = tab === "target" ? targetData : keywordData;
    return (data && typeof data === "object" && "keywords" in data ? (data as KeywordsResponse).keywords : []).map(
      (row) => {
        const ad = adsMapping[row.advertisement_id];
        return {
          ...row,
          orders: row.conversion,
          conversion_rate: row.conv_rate,
          ad_name: ad?.ad_name,
          managed: (ad?.managed ?? "").toLowerCase() === "yes",
        };
      },
    );
  }, [keywordData, targetData, adsMapping, tab]);

  const unmanagedAdIds = useMemo(() => {
    return tableData.filter((ad) => !ad.managed).map((ad) => ad.id);
  }, [tableData]);

  const { prefilledTargets, isAsinTarget } = useMemo(() => {
    let formattedTargetText = dims === "query" ? rowQuery : rowKeywordText;

    let isAsinTarget = false;

    if (formattedTargetText && !!extractAsin(formattedTargetText)) {
      formattedTargetText = extractAsin(formattedTargetText)!;
      isAsinTarget = true;
    }

    const prefilledTargets =
      typeof formattedTargetText !== "string" ? []
      : isAsinTarget ?
        [
          {
            text: formattedTargetText,
            matchTypes: [],
          },
        ]
      : [{ text: formattedTargetText, matchTypes: ["exact", "phrase", "broad"] }];

    return { prefilledTargets, isAsinTarget };
  }, [dims, rowKeywordText, rowQuery]);

  if (
    (isLoading && !useClickhouseSearchTermReportData) ||
    timelineQuery.isLoading ||
    isAdsDataLoading ||
    isTargetDataLoading ||
    isKeywordDataLoading
  ) {
    return <Loading fullPage={false} height={"300px"} />;
  }
  return (
    <Row className="pt-3">
      <Flex
        className={css({
          justifyContent: "space-between",
          alignItems: "center",
          paddingX: "xlarge",
        })}
      >
        <h3
          className="pb-4 px-5"
          style={{
            fontSize: 22,
          }}
        >
          {isFlattenedSearchTermReport ?
            <>
              {t(($) => $.query_funnel.expander["Query: {query}"], { query })}
              <br />{" "}
              {t(($) => $.query_funnel.expander["Keyword Text: {keyword_text}"], {
                keyword_text: String(keyword_text),
              })}{" "}
              <span>{match_type ? `(${match_type})` : ""}</span>
            </>
          : <>
              {query} {keyword_text} <span>{match_type ? `(${match_type})` : ""}</span>
            </>
          }
        </h3>
        {userCanUpdateAds && (
          <AddTargetsWidget
            initialData={{ targets: prefilledTargets }}
            buttonText={
              dims === "query" ?
                t(($) => $.query_funnel.expander["Add Search Query to Media Plans"])
              : t(($) => $.query_funnel.expander["Add Keyword Text to Media Plans"])
            }
            targetType={isAsinTarget ? "target" : "keyword"}
            renderAdSelector={true}
            onReset={() => {
              setSelectedTargets([]);
            }}
            descriptionText={
              dims === "query" ?
                t(
                  ($) =>
                    $.query_funnel.expander[
                      'Add query text to media plans by selecting one below and customizing match types. Once submitted, you can add the query text to more media plans by selecting another and clicking the "Add Keywords" button.'
                    ],
                )
              : t(
                  ($) =>
                    $.query_funnel.expander[
                      'Add keyword text to media plans by selecting one below and customizing match types. Once submitted, you can add the query text to more media plans by selecting another and clicking the "Add Keywords" button.'
                    ],
                )
            }
          />
        )}
      </Flex>

      <Col xs={12}>
        <div className={css({ paddingLeft: "large", paddingRight: "large" })}>
          <Col xs={12}>
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={data}
              group={group}
              handleLegendChange={(e) => {
                const id = e.target.options.custom?.id;
                if (id) toggleMetric(id);
                return false;
              }}
              showAnnotations={false}
            />
          </Col>
          {!isFlattenedSearchTermReport && (
            <>
              <Col className="ph-5 mb-5 border-bottom fs-standard" xs={12}>
                <Nav
                  variant="underline"
                  onSelect={(k) => {
                    setTab(k);
                  }}
                  activeKey={tab ?? undefined}
                >
                  <Nav.Item>
                    <Nav.Link eventKey="keyword">{t(($) => $.query_funnel.expander.Keywords)}</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="target">{t(($) => $.query_funnel.expander.Targets)}</Nav.Link>
                  </Nav.Item>
                  {renderNegativeKeywordsTab && (
                    <Nav.Item>
                      <Nav.Link eventKey="negative-keyword">
                        {t(($) => $.query_funnel.expander["Negative Keywords"])}
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </Col>
              {Array.isArray(tableData) && tableData.length > 0 ?
                <ExpanderTable
                  tableRef={tableRef}
                  tableId={tableId}
                  isExpandable={false}
                  isStatsLoading={isTargetDataLoading}
                  isSelectable={userCanUpdateAds}
                  selectionKey={"target_id"}
                  selected={selectedTargets}
                  setSelected={setSelectedTargets}
                  bulkActionOptions={[...bulkActionOptions, ...additionalBulkActions]}
                  minRows={0}
                  disableSelectionList={unmanagedAdIds}
                  titleCol={{
                    id: "target",
                    name: t(($) => $.query_funnel.expander.Target),
                    Header: <MetricLabel label={t(($) => $.query_funnel.expander.Target)} />,
                    accessor: "text",
                    style: { width: "300px" },
                    width: 300,
                    Cell: (props) => {
                      return (
                        <>
                          <KeywordCell
                            value={formatTargetText(props.original?.text, {
                              ...props.original?.addtl_data,
                              marketPlace: marketPlace,
                            })}
                            columnWidth={300}
                          />
                          <br />
                          <TrellisLink
                            to={getDashboardTo({
                              type: "advertisement",
                              id: props.original?.advertisement_id,
                            })}
                            target="_blank"
                            style={{
                              textDecoration: "none",
                              fontWeight: "500",
                              fontSize: "1.5rem",
                              cursor: "pointer",
                              paddingRight: "1rem",
                            }}
                          >
                            <small>{props.original?.ad_name}</small>
                          </TrellisLink>
                          <TargetBadges target={props.original} />
                        </>
                      );
                    },
                    Filter: () => <div />,
                  }}
                  data={tableData}
                  columnSpecs={columns}
                  getSearchCriteria={(row) => {
                    const typedRow = row as {
                      text: string;
                      addtl_data: Record<string, unknown>;
                      query: string;
                      ad_name: string;
                      ad_group_name: string;
                    };

                    const formattedTargetText = formatTargetText(typedRow.text, {
                      ...typedRow.addtl_data,
                      marketPlace: marketPlace,
                    });
                    const { query, ad_name, ad_group_name } = typedRow;
                    return `${query} ${ad_name} ${ad_group_name} ${formattedTargetText}`;
                  }}
                  tableRowDescription={t(($) => $.query_funnel.expander["Search Terms"])}
                  defaultSorted={[{ id: "impressions", desc: true }]}
                  isLoading={isKeywordDataLoading}
                  filterWidget={true}
                  hideFilterToggleIcon={true}
                  hideResetFiltersIcon={false}
                  excludedFilterColumns={["match_type"]}
                  {...filterProps}
                  additionalFilterOptions={[SEARCH_TERM_REPORT_MATCH_TYPE_FILTER]}
                  handleBulkChanges={() => {}}
                />
              : <Flex
                  className={css({
                    padding: "large",
                  })}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <p>
                    {tab === "keyword" ?
                      t(
                        ($) =>
                          $.query_funnel.expander[
                            "There are no advertised keywords associated with this search query for the report's date range."
                          ],
                      )
                    : tab === "target" ?
                      t(
                        ($) =>
                          $.query_funnel.expander[
                            "There are no advertised targets associated with this search query for the report's date range."
                          ],
                      )
                    : t(
                        ($) =>
                          $.query_funnel.expander[
                            "There are no advertised negative-keywords associated with this search query for the report's date range."
                          ],
                      )
                    }
                  </p>
                </Flex>
              }
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default QueryExpander;
